<!--
📌 Componente: VitrineHeader.vue
📂 Caminho: src/components/VitrineHeader.vue
📝 Descrição: Cabeçalho da Vitrine. Busca, telefone, seletor de idioma
  data-driven (require.context + Intl.DisplayNames) e link de login.

👁️ Interface e Interação com o Usuário:
  - Logo clicável.
  - Busca com sugestões e teclado.
  - Telefone (tel:) abre discador.
  - Botão de idioma com lista dinâjustify-betweemica.
  - Login por router-link.

📥 Entrada (Props):
  - `modelValue` (String)
  - `isScrolled` (Boolean)
  - `suggestions` (Array<String>)
  - `phoneNumber` (String)
  - `phoneLabel` (String)

📤 Saída (Emits):
  - `update:modelValue`, `focus-search`, `select-suggestion`,
    `logo-click`, `open-lang`, `contact-click`

🔗 Dependências:
  - **Utilitários JS:** i18nVitrine.*
  - **Bibliotecas externas:** vue-i18n, TailwindCSS.
-->

<template>
  <header
    class="fixed top-0 w-full z-40 transition-all duration-300"
    :class="{
      'bg-branco shadow-md': isScrolled,
      'bg-cinza-claro': !isScrolled,
    }"
    role="banner"
  >
    <div class="container mx-auto px-4 py-4 sm:py-6">
      <div
        :class="[
          'flex',
          'flex-row',
          'flex-wrap',
          'md:flex-nowrap',
          'items-center',
          'justify-between',
          'text-azul-profundo',
          'gap-2',
          'sm:gap-3',
        ]"
      >
        <!-- Logo + Kontakt (mobile) -->
        <div :class="['flex w-full items-center', 'md:w-auto', 'md:gap-0']">
          <a
            href="/"
            :class="[
              'flex-1',
              'flex',
              'justify-center',
              'text-center md:text-left',
              'md:flex-none',
              'md:w-auto',
              'md:justify-start',
            ]"
            @click.prevent="$emit('logo-click')"
            aria-label="Home"
          >
            <!-- h1 invisível para SEO/A11y -->
            <span class="sr-only">Praxis Secondhand</span>
            <!-- Imagens: logo + texto com srcset 1x/2x -->
            <div
              :class="[
                'inline-flex',
                'items-center',
                'gap-2',
                'w-full',
                'justify-center',
                'md:w-auto',
                'md:justify-start',
                'md:flex-shrink-0',
              ]"
            >
              <img
                :src="logo64"
                :srcset="logo64 + ' 1x, ' + logo128 + ' 2x'"
                :class="[
                  'h-auto',
                  'w-auto',
                  'shrink-0',
                  'max-h-10',
                  'lg:max-h-12',
                ]"
                alt="System Praxis Secondhand Logo"
                loading="eager"
                decoding="async"
                fetchpriority="high"
              />
              <img
                :src="text64"
                :srcset="text64 + ' 1x, ' + text128 + ' 2x'"
                :class="[
                  'h-auto',
                  'w-auto',
                  'min-w-0',
                  'shrink',
                  'max-h-10',
                  'lg:max-h-12',
                ]"
                alt="Praxis Secondhand"
                loading="eager"
                decoding="async"
                fetchpriority="high"
              />
            </div>
          </a>
          <button
            type="button"
            :class="[
              'md:hidden',
              'inline-flex items-center',
              'text-sm font-bold text-azul-profundo',
              'hover:text-azul-hover',
              'focus-visible:outline-none',
              'focus-visible:ring-2 focus-visible:ring-foco',
              'rounded',
              'ml-3',
            ]"
            @click="onContactClick"
          >
            {{ $t("vitrine.header.contact") }}
          </button>
        </div>
        <!-- Busca -->
        <div
          :class="[
            'relative',
            'flex-1',
            'min-w-0',
            'w-auto',
            'md:max-w-2xl',
            'lg:max-w-3xl',
            'my-2',
            'md:my-0',
          ]"
        >
          <label :for="ids.input" class="sr-only">
            {{ $t("vitrine.searchLabel") }}
          </label>
          <input
            :id="ids.input"
            v-model="localQuery"
            type="search"
            enterkeyhint="search"
            autocapitalize="none"
            spellcheck="false"
            :placeholder="$t('vitrine.searchPlaceholder')"
            :class="[
              'w-full',
              'pl-10',
              'pr-10',
              'py-2',
              'rounded',
              'border',
              'focus:outline-none',
              'focus:ring-2',
              'focus:ring-azul-profundo',
            ]"
            @focus="onFocus"
            @blur="onBlur"
            @keydown.down.prevent="move(1)"
            @keydown.up.prevent="move(-1)"
            @keydown.enter.prevent="onEnter"
            @keydown.esc="closeSuggestions"
            autocomplete="off"
            aria-autocomplete="list"
            :aria-controls="ids.listbox"
            :aria-activedescendant="
              activeIndex >= 0 ? optionId(activeIndex) : undefined
            "
            :aria-expanded="open.toString()"
            role="combobox"
          />
          <!-- Ícone lupa -->
          <svg
            v-once
            xmlns="http://www.w3.org/2000/svg"
            :class="[
              'h-5 w-5',
              'absolute left-3 top-2.5',
              'pointer-events-none',
            ]"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
            aria-hidden="true"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M21 21l-4.35-4.35
       M11 19a8 8 0 1 0 0-16
       8 8 0 0 0 0 16z"
            />
          </svg>

          <!-- Botão limpar -->
          <button
            v-if="localQuery"
            type="button"
            @click="clearQuery"
            :aria-label="$t('vitrine.searchClear')"
            :class="[
              'absolute right-2 top-1.5',
              'p-1 rounded',
              'text-cinza-400',
              'hover:text-texto-secundario',
              'focus-visible:outline-none',
              'focus-visible:ring-2',
              'focus-visible:ring-azul-profundo',
            ]"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          <!-- Sugestões -->
          <ul
            v-if="open"
            :id="ids.listbox"
            role="listbox"
            :class="[
              'absolute z-20 mt-1 w-full',
              'bg-branco border rounded shadow-sm',
              'max-h-64 overflow-auto',
            ]"
          >
            <li
              v-if="!suggestions || !suggestions.length"
              class="px-3 py-2 text-cinza-500"
              aria-disabled="true"
            >
              {{ $t("vitrine.searchNoResults") }}
            </li>

            <li
              v-for="(s, i) in suggestions"
              :key="`${s}-${i}`"
              :id="optionId(i)"
              role="option"
              :aria-selected="i === activeIndex"
              @mousedown.prevent="select(s)"
              @mousemove="activeIndex = i"
              :class="[
                'px-3 py-2 cursor-pointer',
                'hover:bg-cinza-claro focus:bg-cinza-claro',
                { 'bg-cinza-claro': i === activeIndex },
              ]"
              v-html="highlight(s)"
            />
          </ul>
        </div>
        <!-- Ações: Tel, Idioma, Login -->
        <div
          :class="['w-auto', 'text-center', 'md:text-right', 'flex-shrink-0']"
        >
          <div
            :class="[
              'flex',
              'items-center',
              'justify-end',
              'space-x-4',
              'flex-nowrap',
              'whitespace-nowrap',
            ]"
          >
            <button
              type="button"
              :class="[
                'hidden md:inline-flex',
                'text-sm font-bold text-azul-profundo',
                'hover:text-azul-hover',
                'focus-visible:outline-none',
                'focus-visible:ring-2 focus-visible:ring-foco',
                'rounded',
              ]"
              @click="onContactClick"
            >
              {{ $t("vitrine.header.contact") }}
            </button>
            <!-- Telefone -->
            <a
              :href="`tel:${phoneNumber}`"
              :class="[
                'text-sm font-bold flex items-center text-azul-profundo',
                'hover:text-azul-hover',
                'focus-visible:outline-none',
                'focus-visible:ring-2 focus-visible:ring-foco',
                'rounded',
              ]"
              :aria-label="callAria"
              @click="
                analytics &&
                  logEvent(analytics, 'contact_click', { type: 'phone' })
              "
            >
              <svg
                v-once
                xmlns="http://www.w3.org/2000/svg"
                class="h-6 w-6 xl:mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M3 5a2 2 0 0 1 2-2h3.28a1 1 0 0 1 .95.68
                     l1.5 4.49a1 1 0 0 1-.5 1.21l-2.26 1.13a11.04
                     11.04 0 0 0 5.52 5.52l1.13-2.26a1 1 0 0 1
                     1.21-.5l4.49 1.5a1 1 0 0 1 .68.95V19a2 2 0 0
                     1-2 2h-1C9.72 21 3 14.28 3 6V5z"
                />
              </svg>
              <span class="hidden xl:inline">{{ phoneLabel }}</span>
            </a>
            <!-- Idioma -->
            <div class="relative">
              <button
                class="p-1 text-azul-profundo hover:text-azul-hover"
                :aria-label="$t('vitrine.lang.aria')"
                @click="toggleLang()"
                type="button"
                aria-haspopup="listbox"
                :aria-expanded="showLang.toString()"
              >
                <svg
                  v-once
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="2"
                  aria-hidden="true"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M21 12a9 9 0 0 1-9 9
                       m9-9a9 9 0 0 0-9-9
                       M21 12H3
                       m9 9a9 9 0 0 1-9-9
                       m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9
                       m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9
                       m-9 9a9 9 0 0 1 9-9"
                  />
                </svg>
              </button>
              <ul
                v-if="showLang"
                :class="[
                  'absolute mt-2 right-0',
                  'w-56 sm:w-64',
                  'bg-branco border rounded shadow',
                  'z-30 text-left',
                  'max-h-80 overflow-y-auto overscroll-contain pr-1',
                ]"
                role="listbox"
                @keydown.esc.prevent.stop="showLang = false"
              >
                <li v-for="lc in sortedLocales" :key="lc">
                  <button
                    :class="[
                      'w-full text-left px-3 py-2 hover:bg-cinza-claro',
                      'flex items-center justify-between',
                    ]"
                    role="option"
                    :aria-selected="lc === locale"
                    @click="changeLang(lc)"
                  >
                    <span>{{ label(lc) }}</span>
                    <span v-if="lc === locale" aria-hidden="true">✓</span>
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import {
  getVitrineLocales,
  displayName,
  setVitrineLocaleComposer,
  ensureVitrineLocaleComposer,
  getCachedVitrineLocale,
} from "../i18nVitrine";
import { computed, onMounted, onBeforeUnmount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { analytics, logEvent } from "@/firebaseConfig";
/* 🖼️ Imports de imagens WebP (64/128 px) */
// Comentário: usar bundler para resolver assets do /src.
import logo64 from "@/assets/images/praxis-64.webp";
import logo128 from "@/assets/images/praxis-128.webp";
import text64 from "@/assets/images/logo_relectro-64.webp";
import text128 from "@/assets/images/logo_relectro-128.webp";

export default {
  name: "VitrineHeader",
  props: {
    modelValue: { type: String, default: "" },
    isScrolled: { type: Boolean, default: false },
    suggestions: { type: Array, default: () => [] },
    phoneNumber: { type: String, default: "+4964218733319" },
    phoneLabel: { type: String, default: "06421 87333-19" },
  },
  emits: [
    "update:modelValue",
    "focus-search",
    "select-suggestion",
    "logo-click",
    "open-lang",
    "contact-click",
  ],
  setup(props, { emit }) {
    // composer: locale (Ref), merge/getLocaleMessage
    // usa o composer global para refletir no app inteiro
    const composer = useI18n({ useScope: "global" });
    const { locale, t } = composer;

    // Idiomas detectados via vitrine-*.json
    const locales = ref(getVitrineLocales());

    // Ordena alfabeticamente pelo rótulo exibido no idioma atual.
    // Recalcula ao mudar locale ou a lista de idiomas.
    const sortedLocales = computed(() => {
      return [...locales.value].sort((a, b) =>
        label(a).localeCompare(label(b))
      );
    });

    // Aria do telefone via i18n (evita linha longa no template)
    const callAria = computed(() =>
      t("vitrine.callNumberAria", { num: props.phoneLabel })
    );

    const onContactClick = () => {
      if (analytics) {
        logEvent(analytics, "contact_click", { type: "header_button" });
      }
      emit("contact-click");
    };

    // Garante mensagens e aplica cache da Vitrine
    onMounted(async () => {
      const cached = getCachedVitrineLocale();
      if (cached && locales.value.includes(cached)) {
        await setVitrineLocaleComposer(composer, cached);
      } else {
        await ensureVitrineLocaleComposer(composer, locale.value);
      }
      document.addEventListener("click", onDocClick);
      document.addEventListener("keydown", onGlobalKey);
    });

    onBeforeUnmount(() => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onGlobalKey);
    });

    const showLang = ref(false);
    const toggleLang = () => {
      showLang.value = !showLang.value;
    };

    function label(code) {
      return displayName(code, locale);
    }

    async function changeLang(lc) {
      await setVitrineLocaleComposer(composer, lc);
      showLang.value = false;
    }

    // Busca
    const localQuery = ref(props.modelValue);
    let emitDebounce = 0;
    watch(
      () => props.modelValue,
      (v) => {
        if (v !== localQuery.value) localQuery.value = v;
      }
    );
    watch(localQuery, (v) => {
      clearTimeout(emitDebounce);
      emitDebounce = window.setTimeout(() => {
        emit("update:modelValue", v);
      }, 200);
    });

    // Listbox controlado por foco + resultados
    const open = ref(false);
    const hasFocus = ref(false);
    const activeIndex = ref(-1);

    function onFocus() {
      emit("focus-search");
      hasFocus.value = true;
      open.value = !!props.suggestions?.length;
    }
    function onBlur() {
      hasFocus.value = false;
      open.value = false;
      activeIndex.value = -1;
    }
    watch(
      () => props.suggestions,
      (list) => {
        open.value = hasFocus.value && !!(list && list.length);
      }
    );

    // Enter: seleciona ativo ou apenas fecha
    function onEnter() {
      if (activeIndex.value >= 0) {
        const s = props.suggestions[activeIndex.value];
        select(s);
        return;
      }
      open.value = false;
    }

    const ids = computed(() => ({
      input: "vitrine-search-input",
      listbox: "vitrine-search-listbox",
    }));

    const optionId = (i) => `vitrine-option-${i}`;

    function move(delta) {
      if (!props.suggestions?.length) return;
      const max = props.suggestions.length - 1;
      const next = activeIndex.value + delta;
      activeIndex.value = next < 0 ? max : next > max ? 0 : next;
    }

    function confirmActive() {
      if (activeIndex.value < 0) return;
      const s = props.suggestions[activeIndex.value];
      select(s);
    }

    function select(s) {
      emit("select-suggestion", s);
      emit("update:modelValue", s);
      open.value = false;
      activeIndex.value = -1;
    }

    function closeSuggestions() {
      open.value = false;
      activeIndex.value = -1;
    }

    /* destaque do termo nas sugestões (case-insensitive) */
    function highlight(s) {
      const txt = String(s || "");
      const q = String(localQuery.value || "").trim();
      if (!q) return escapeHtml(txt);
      const re = new RegExp(q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "ig");
      return escapeHtml(txt).replace(re, (m) => `<mark>${m}</mark>`);
    }
    function escapeHtml(v) {
      return v.replace(
        /[&<>"]/g,
        (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c])
      );
    }

    /* Ctrl/Cmd+K foca a busca */
    function onGlobalKey(e) {
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === "k") {
        e.preventDefault();
        const el = document.getElementById(ids.value.input);
        if (el) el.focus();
      }
    }

    /* Limpa a busca e reseta highlight de sugestão */
    function clearQuery() {
      localQuery.value = "";
      emit("update:modelValue", "");
      activeIndex.value = -1;
      open.value = false;
    }

    function onDocClick(e) {
      const header = e.target.closest("header");
      if (!header) {
        closeSuggestions();
        showLang.value = false;
      }
    }

    return {
      // busca
      localQuery,
      open,
      activeIndex,
      ids,
      optionId,
      move,
      confirmActive,
      select,
      closeSuggestions,
      clearQuery,
      onFocus,
      onBlur,
      onEnter,
      highlight,
      // idioma
      showLang,
      toggleLang,
      changeLang,
      locale,
      locales,
      sortedLocales,
      label,
      callAria,
      onContactClick,
      // imagens do cabeçalho
      logo64,
      logo128,
      text64,
      text128,
    };
  },
};
</script>

<style scoped>
/* Acessibilidade: foco visível coberto pelas classes Tailwind. */
</style>
